import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientSatisfactionSurveyService } from './../../../../core/services/patient-satisfaction-survey.service';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
    selector: 'app-patient-survey-responses',
    templateUrl: './patient-survey-responses.component.html',
    styleUrls: ['./patient-survey-responses.component.scss']
})
export class PatientSurveyResponsesComponent extends BaseComponent implements OnInit {

    @Input() initialExpanded?: boolean;
    @Input() patient?: any;
    @Input() selectedGroup?: any;
    /** A state unit (tasks/state-mode.md): no survey is sent to the patient. */
    get stateMode(): boolean {
        return isStateModeGroup(this.selectedGroup);
    }
    expanded?: boolean;
    @Output() expandedChange = new EventEmitter<boolean>();

    surveyData: any[] | null = null;
    groupedSurveyData: any[] | null = null;

    constructor(
        private patientSatisfactionSurveyService: PatientSatisfactionSurveyService,
        fs: FeedbackService,
        md: MatDialog
    ) {
        super(fs, md);
    }

    ngOnInit(): void {
        this.loadSurveyData();
    }

    toggleExpanded() {
        this.expanded = !this.expanded;
        this.expandedChange.emit(this.expanded);
    }

    loadSurveyData(): void {
        this.patientSatisfactionSurveyService.patientSurveys$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (surveys) => {
                    this.surveyData = surveys;
                    // Initialize showResponses property for each survey
                    if (surveys) {
                        surveys.forEach((survey: any) => {
                            survey.showResponses = false;
                        });
                    }
                    this.groupedSurveyData = this.groupSurveysByDate(surveys || []);
                },
                error: (err) => {
                    console.error('Error loading surveys:', err);
                    this.surveyData = [];
                    this.groupedSurveyData = [];
                }
            });
    }

    groupSurveysByDate(surveys: any[]): any[] {
        if (!surveys || surveys.length === 0) {
            return [];
        }

        // Group surveys by date (excluding time)
        const grouped = surveys.reduce((groups: any, survey: any) => {
            const dateKey = moment(survey.createdAt).format('YYYY-MM-DD');
            const dateDisplay = moment(survey.createdAt).format('DD/MM/YYYY');

            if (!groups[dateKey]) {
                groups[dateKey] = {
                    date: dateKey,
                    dateDisplay: dateDisplay,
                    surveys: []
                };
            }

            groups[dateKey].surveys.push(survey);
            return groups;
        }, {});

        // Convert to array and sort by date (newest first)
        return Object.values(grouped).sort((a: any, b: any) =>
            moment(b.date).valueOf() - moment(a.date).valueOf()
        );
    }

    getSurveyScore(survey: any): number {
        return Math.round(survey.responses?.score || 0);
    }

    getSurveyScoreDisplay(survey: any): string {
        const score = survey.responses?.score || 0;
        return score;
    }

    getSurveyStatus(survey: any): string {
        if (survey.completed) {
            return 'Completed';
        } else if (new Date(survey.surveyExpiry) < new Date()) {
            return 'Expired';
        } else {
            return 'Pending';
        }
    }

    getSurveyStatusClass(survey: any): string {
        if (survey.completed) {
            return 'completed';
        } else if (new Date(survey.surveyExpiry) < new Date()) {
            return 'expired';
        } else {
            return 'pending';
        }
    }

    formatDate(date: string, includeTime: boolean = true): string {
        return moment(date).format(includeTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');
    }

    getResponseText(survey: any, questionId: string): string {
        if (!survey.responses || !survey.responses[questionId]) {
            return 'No response';
        }
        return survey.responses[questionId];
    }

    getQuestionText(survey: any, questionId: string): string {
        if (!survey.surveyConfig?.sections) {
            return questionId;
        }

        for (const section of survey.surveyConfig.sections) {
            for (const question of section.questions) {
                if (question.id === questionId) {
                    return question.question;
                }
            }
        }
        return questionId;
    }

    getOptionText(survey: any, questionId: string, value: string): string {
        if (!survey.surveyConfig?.sections) {
            return value;
        }

        for (const section of survey.surveyConfig.sections) {
            for (const question of section.questions) {
                if (question.id === questionId && question.options) {
                    const option = question.options.find((opt: any) => opt.value === value);
                    return option ? option.text : value;
                }
            }
        }
        return value;
    }

    getQuestionType(survey: any, questionId: string): string {
        if (!survey.surveyConfig?.sections) {
            return 'text';
        }

        for (const section of survey.surveyConfig.sections) {
            for (const question of section.questions) {
                if (question.id === questionId) {
                    return question.type;
                }
            }
        }
        return 'text';
    }

    getSectionTitle(survey: any, sectionId: string): string {
        if (!survey.surveyConfig?.sections) {
            return sectionId;
        }

        const section = survey.surveyConfig.sections.find((s: any) => s.id === sectionId);
        return section ? section.title : sectionId;
    }

    getQuestionsBySection(survey: any, sectionId: string): any[] {
        if (!survey.surveyConfig?.sections) {
            return [];
        }

        const section = survey.surveyConfig.sections.find((s: any) => s.id === sectionId);
        return section ? section.questions : [];
    }

    getSections(survey: any): any[] {
        return survey.surveyConfig?.sections || [];
    }

    hasResponses(survey: any): boolean {
        return survey.responses && Object.keys(survey.responses).length > 0;
    }

    hasSectionResponses(survey: any, sectionId: string): boolean {
        if (!survey.responses || !survey.surveyConfig?.sections) {
            return false;
        }

        const section = survey.surveyConfig.sections.find((s: any) => s.id === sectionId);
        if (!section) {
            return false;
        }

        return section.questions.some((question: any) => survey.responses[question.id]);
    }

    copySurveyData(survey: any): void {
        const surveyText = this.formatSurveyForCopy(survey);
        navigator.clipboard.writeText(surveyText).then(() => {
            this.showToastMessage('Survey data copied to clipboard');
        }).catch(err => {
            this.showToastMessage('Failed to copy to clipboard');
        });
    }

    private formatSurveyForCopy(survey: any): string {
        let text = `Patient Satisfaction Survey\n`;
        text += `Patient: ${survey.Patient?.name} ${survey.Patient?.surname}\n`;
        text += `Survey: ${survey.surveyTitle}\n`;
        text += `Status: ${this.getSurveyStatus(survey)}\n`;
        text += `Score: ${this.getSurveyScoreDisplay(survey)}/5\n`;
        text += `Created: ${this.formatDate(survey.createdAt)}\n`;
        if (survey.completedAt) {
            text += `Completed: ${this.formatDate(survey.completedAt)}\n`;
        }
        if (survey.PatientAppointment) {
            text += `Appointment: ${this.formatDate(survey.PatientAppointment.date, false)} ${survey.PatientAppointment.timeSlot}\n`;
        }
        text += `\n`;

        if (survey.responses) {
            for (const section of this.getSections(survey)) {
                text += `${section.title}\n`;
                text += `-`.repeat(section.title.length) + `\n`;

                for (const question of section.questions) {
                    const response = survey.responses[question.id];
                    if (response) {
                        text += `Q: ${question.question}\n`;
                        if (question.type === 'radio') {
                            text += `A: ${this.getOptionText(survey, question.id, response)}\n`;
                        } else {
                            text += `A: ${response}\n`;
                        }
                        text += `\n`;
                    }
                }
            }
        }

        return text;
    }

    toggleSurveyResponses(survey: any): void {
        survey.showResponses = !survey.showResponses;
    }

    deleteSurvey(survey: any): void {
        // Show confirmation dialog
        this.showConfirmationDialog(
            'Delete Survey',
            `Are you sure you want to delete this expired survey for ${survey.Patient?.name} ${survey.Patient?.surname}?`,
            undefined,
            'Cancel',
            'Delete'
        ).then(result => {
            if (result) {
                this.patientSatisfactionSurveyService.deleteSurvey(survey.id).subscribe({
                    next: () => {
                        this.showToastMessage('Survey deleted successfully');
                        // The socket listener will automatically update the list
                    },
                    error: (error: any) => {
                        console.error('Error deleting survey:', error);
                        this.showToastMessage('Failed to delete survey');
                    }
                });
            }
        });
    }

    sendManualSurvey(event: any): void {
        event.stopPropagation();
        
        if (!this.patient || !this.selectedGroup) {
            this.showToastMessage('Patient or group information not available');
            return;
        }

        if (!this.patient.email && !this.patient.mobileNumber) {
            this.showToastMessage('Patient must have email or mobile number to send survey');
            return;
        }

        this.patientSatisfactionSurveyService.createManualSurvey(this.selectedGroup.id, this.patient.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.showToastMessage('Satisfaction survey sent successfully!');
                    // The survey will be automatically loaded via the socket listener
                },
                error: (error) => {
                    console.error('Error sending survey:', error);
                    this.showToastMessage('Failed to send survey. Please try again.');
                }
            });
    }
} 